import { LitElement, nothing, html } from 'lit';
import './custom-function-editor-steps/order-tool-editor-step.js';
import './custom-function-editor-steps/address-tool-editor-step.js';

/**
 * @typedef {function} StepTemplateFunction
 * @param {object} propertyList - The list of properties a step can choose from
 * @param {function} commitCallback - A function to call when the step is finished
 * @returns {import('lit').TemplateResult} - The template result
 */

/**
 * @typedef {object} CustomEditorStep
 * @property {string} stepTitle - The title of the editor step in the model editor
 * @property {StepTemplateFunction} stepTemplate - A function which returns an html interface for the editor step
 */

/**
 * @overload
 * @param {string} pathOrData
 * @param {Object} data
 * @return {void}
 */
/**
 * @overload
 * @param {Object} pathOrData
 * @return {void}
 */
/**
 * Sets the editor's _itemData, or a subpath of _itemData, to the given value.
 * @param {string | Object} pathOrData
 * @param {Object} [data]
 */
export function SetItemData(pathOrData, data) {
  const detail = {};
  // Handle case where only data is supplied and is meant to overwrite the entire item data.
  if (data === undefined) {
    detail.value = pathOrData;
  }
  // Handle the case where a subpath is supplied and the data meant to be written to that subpath.
  else if (typeof pathOrData === 'string' && data !== undefined) {
    detail.path = pathOrData;
    detail.value = data;
  }
  // Error if arguments apply to neither case.
  else {
    throw new Error('Invalid arguments');
  }

  this.dispatchEvent(new CustomEvent('set-item-data', { detail, bubbles: true, composed: true }));
}

export class KatapultCustomFunctionEditorStep extends LitElement {
  static get properties() {
    return {
      _stepPropertyList: { type: Object, state: true },

      // property list for custom steps
      toolItemData: { type: Object },
      otherAttributes: { type: Object }
    };
  }

  /**
   * A lookup that maps a custom function to a custom editor step
   * DEVELOPER NOTE: This is where you add new steps for custom functions.
   * @returns {Record<string, CustomEditorStep>}
   */
  static get FUNCTION_TO_STEP_LOOKUP() {
    return {
      _button_order_nodes: {
        stepTitle: 'Naming Convention',
        stepTemplate: (propertyList) => html`
          <order-tool-editor-step
            .toolItemData="${propertyList.toolItemData}"
            .otherAttributes="${propertyList.otherAttributes}"
            style="width: 100%;"
          ></order-tool-editor-step>
        `
      },
      _button_address_data: {
        stepTitle: 'Configuration',
        stepTemplate: (propertyList) => html`
          <address-tool-editor-step
            .toolItemData="${propertyList.toolItemData}"
            .otherAttributes="${propertyList.otherAttributes}"
            style="width: 100%;"
          ></address-tool-editor-step>
        `
      }
    };
  }

  constructor() {
    super();
    this.toolItemData = {};
    this._stepPropertyList = {};
  }

  /** Get and set all properties that don't have the "state" property set to true */
  setPropertyList() {
    const propertyKeys = Object.keys(KatapultCustomFunctionEditorStep.properties);
    const stepPropertyList = {};
    propertyKeys.forEach((propertyKey) => {
      const isStateProperty = KatapultCustomFunctionEditorStep.properties[propertyKey].state;
      if (!isStateProperty) stepPropertyList[propertyKey] = this[propertyKey];
    });
    this._stepPropertyList = stepPropertyList;
  }

  /** When any attribute changes, reset the property list so the change gets included */
  willUpdate() {
    this.setPropertyList();
  }

  render() {
    const functionForEditorStep = this.toolItemData?.function;
    const stepData = KatapultCustomFunctionEditorStep.FUNCTION_TO_STEP_LOOKUP[functionForEditorStep];
    const stepElement = stepData?.stepTemplate(this._stepPropertyList);
    return stepElement ?? nothing;
  }
}
customElements.define('katapult-custom-function-editor-step', KatapultCustomFunctionEditorStep);
